import React, { memo, useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { CoGuardianMutualKidVM } from '@/features/guardian-player/types';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import MutualKidCard from './mutual-kid-card';

const strings = STRINGS.guardianPlayer.coGuardianProfile;
const TABLET_COLUMNS = 2;

type MutualKidsSectionProps = {
  kids: CoGuardianMutualKidVM[];
  onPressKid: (kid: CoGuardianMutualKidVM) => void;
};

/**
 * "Mutual Kids" title + kid cards. Tablet lays them out as a 2-up grid (a lone card in the
 * last row stays half-width unless it's the only kid, in which case it fills the row); phone
 * keeps a single stacked column.
 */
function MutualKidsSection({ kids, onPressKid }: MutualKidsSectionProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const kidRows = useMemo(() => {
    const rows: CoGuardianMutualKidVM[][] = [];
    for (let index = 0; index < kids.length; index += TABLET_COLUMNS) {
      rows.push(kids.slice(index, index + TABLET_COLUMNS));
    }
    return rows;
  }, [kids]);

  return (
    <Stack gap={scale(12)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {strings.mutualKidsTitle}
      </Text>

      {isTablet ? (
        <Stack gap={scale(8)}>
          {kidRows.map(row => (
            <Stack key={row[0].id} direction="row" gap={scale(8)}>
              {row.map(kid => (
                <Stack key={kid.id} flex={1} minWidth={0}>
                  <MutualKidCard kid={kid} onPress={onPressKid} />
                </Stack>
              ))}
              {row.length === 1 && kids.length > 1 ? <Stack flex={1} minWidth={0} /> : null}
            </Stack>
          ))}
        </Stack>
      ) : (
        kids.map(kid => <MutualKidCard key={kid.id} kid={kid} onPress={onPressKid} />)
      )}
    </Stack>
  );
}

export default memo(MutualKidsSection);

const useStyles = createStyles(() => ({
  title: {
    color: guardianColors['10'],
  },
  empty: {
    color: guardianColors['6'],
  },
}));
